import fuzzysort from 'fuzzysort';

import { DayjsUnit } from '@/models/Dayjs.ts';
import { Room, RoomType } from '@/models/Room.ts';
import { SelectionOption } from '@/models/SelectionOption.ts';
import { DailyAppointment } from '@/models/appointment/Appointment.ts';
import { PVSUserRoleMapping } from '@/models/pvs/UserRoleMapping.ts';

import { getDateOfBirthSearchValues } from '@/utils/date-utils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';

// Filter appointments based on search text and date
export const filterAppointments = (
    appointments: DailyAppointment[],
    searchText: string,
    date: Date | undefined,
    practitionerIds: string[]
): DailyAppointment[] => {
    const baseFiltered = appointments.filter((appointment) => {
        const matchesDate =
            !date || (appointment?.start ? dayjs(appointment.start).isSame(dayjs(date), DayjsUnit.DAY) : false);
        const matchesPractitioner = practitionerIds.includes(appointment.professional.id);
        return matchesDate && matchesPractitioner;
    });

    const search = searchText.trim();
    if (!search) return baseFiltered;

    const results = fuzzysort.go(search, baseFiltered, {
        keys: [
            (appointment): string => appointment.patient?.name?.first_name ?? '',
            (appointment): string => appointment.patient?.name?.last_name ?? '',
            (appointment): string => appointment.patient?.contact?.email ?? '',
            (appointment): string => String(appointment.patient?.account?.patient_number ?? ''),
            (appointment): string => getDateOfBirthSearchValues(appointment.patient?.account?.date_of_birth).join(' '),
        ],
        threshold: 0.3,
    });

    const matched = new Set(results.map((result) => result.obj));
    // Keep the board's original appointment order instead of fuzzysort's relevance order
    return baseFiltered.filter((appointment) => matched.has(appointment));
};

/**
 * Filter rooms based on selected room IDs. Always includes waiting rooms.
 * @param rooms - Array of all rooms
 * @param selectedRoomIds - Array of selected room IDs to filter by
 * @returns Filtered array of rooms with waiting rooms always included
 */
export const filterRoomsBySelection = (rooms: Room[] = [], selectedRoomIds: string[] = []): Room[] => {
    if (rooms.length === 0) return [];

    // Get waiting rooms - always visible
    const waitingRooms = rooms.filter((room) => room.room_type === RoomType.WAITING_ROOM);

    // Get filtered treatment rooms based on selection
    const treatmentRooms =
        selectedRoomIds.length > 0
            ? rooms.filter((room) => room.room_type === RoomType.ROOM && selectedRoomIds.includes(room.room_id))
            : [];

    // Combine waiting rooms with filtered treatment rooms
    return [...waitingRooms, ...treatmentRooms];
};

/** Roles fallback: derive options from practitioners' currently-known roles, used while the DC roles are loading or unreachable. */
export const getFallbackRoleOptions = (
    pvsUserRoles: PVSUserRoleMapping[],
    instanceIds: string[]
): SelectionOption[] => {
    const filtered = pvsUserRoles.filter((r) => instanceIds.includes(r.instance_id));
    const uniqueRoles = new Set<string>(filtered.flatMap((pvsUserRole) => pvsUserRole.roles));
    return [...uniqueRoles]
        .map((role) => ({ label: role, value: role }))
        .sort((a, b) => a.label.localeCompare(b.label));
};
